import * as THREE from "three";
import type { IGameState, IPlayerState, IGhostState } from "@shared/types";
import { scene, camera } from "./core";
import {
  getPlayerMeshes,
  getGhostMeshes,
  getDotMeshes,
  getWallMeshes,
} from "./gameState";
import { createMazeCell } from "./sceneObjects";
import { createPlayerMesh } from "./models/playerModel";
import { createGhostMesh } from "./models/ghostModel";

export function initializeGameWorld(state: IGameState): void {
  const playerMeshes = getPlayerMeshes();
  const ghostMeshes = getGhostMeshes();
  const dotMeshes = getDotMeshes();
  const wallMeshes = getWallMeshes();

  while (scene.children.length > 0) {
    scene.remove(scene.children[0]);
  }

  const ambientLight = new THREE.AmbientLight(0x404040, 0.7);
  scene.add(ambientLight);

  const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
  directionalLight.position.set(5, 15, 5);
  directionalLight.castShadow = true;
  directionalLight.shadow.mapSize.width = 1024;
  directionalLight.shadow.mapSize.height = 1024;
  directionalLight.shadow.camera.near = 0.5;
  directionalLight.shadow.camera.far = 50;
  directionalLight.shadow.camera.left = -15;
  directionalLight.shadow.camera.right = 15;
  directionalLight.shadow.camera.top = 15;
  directionalLight.shadow.camera.bottom = -15;
  scene.add(directionalLight);

  for (const key in playerMeshes) {
    delete playerMeshes[key];
  }

  for (const key in ghostMeshes) {
    delete ghostMeshes[key];
  }

  dotMeshes.length = 0;

  for (const key in wallMeshes) {
    delete wallMeshes[key];
  }

  const mazeSize = state.mazeSize || 20;
  const groundScale = mazeSize > 30 ? 1.5 : 2;
  const groundSize = mazeSize * groundScale;
  const groundGeometry = new THREE.PlaneGeometry(
    groundSize,
    groundSize,
    32,
    32
  );

  const groundMaterial = new THREE.MeshLambertMaterial({
    color: 0x000033,
    emissive: 0x000022,
    side: THREE.DoubleSide,
  });

  const ground = new THREE.Mesh(groundGeometry, groundMaterial);
  ground.rotation.x = -Math.PI / 2;
  ground.position.set(mazeSize / 2, -0.02, mazeSize / 2);
  ground.receiveShadow = true;
  scene.add(ground);

  scene.fog = new THREE.FogExp2(0x000022, 0.03);

  const gridHelper = new THREE.GridHelper(
    groundSize,
    groundSize,
    0x0000ff,
    0x000066
  );
  gridHelper.position.set(mazeSize / 2, -0.01, mazeSize / 2);
  gridHelper.material.transparent = true;
  gridHelper.material.opacity = 0.15;
  scene.add(gridHelper);

  for (const cell of state.maze) {
    createMazeCell(cell, wallMeshes, dotMeshes);
  }

  for (const [id, player] of Object.entries(state.players)) {
    playerMeshes[id] = createPlayerMesh(player as IPlayerState);
  }

  for (const [id, ghost] of Object.entries(state.ghosts)) {
    ghostMeshes[id] = createGhostMesh(ghost as IGhostState);
  }

  const cameraHeight = Math.max(10, mazeSize / 2);
  const cameraDistance = Math.max(5, mazeSize / 4);

  const centerX = mazeSize / 2;
  const centerZ = mazeSize / 2;
  camera.position.set(centerX, cameraHeight, centerZ + cameraDistance);
  camera.lookAt(centerX, 0, centerZ);

  const loadingElement = document.getElementById("loading");
  if (loadingElement) {
    loadingElement.style.display = "none";
  }
}
